import React, { Suspense, lazy, memo } from 'react';
import { motion } from 'framer-motion';

// Memoized components for better performance
const MemoizedCard = memo(({ title, description, icon }) => (
  <motion.div
    className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300"
    whileHover={{ y: -5, scale: 1.02 }}
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4 }}
  >
    <div className="text-bronze-600 mb-4">{icon}</div>
    <h3 className="text-xl text-gray-900 mb-2 font-serif">{title}</h3>
    <p className="text-gray-600 font-serif">{description}</p>
  </motion.div>
));

// Virtual scrolling for large lists
const VirtualList = ({ items, itemHeight = 100, containerHeight = 400 }) => {
  const [scrollTop, setScrollTop] = React.useState(0);
  
  const visibleItems = React.useMemo(() => {
    const startIndex = Math.floor(scrollTop / itemHeight);
    const endIndex = Math.min(startIndex + Math.ceil(containerHeight / itemHeight), items.length);
    
    return items.slice(startIndex, endIndex).map((item, index) => ({
      ...item,
      index: startIndex + index,
    }));
  }, [items, itemHeight, containerHeight, scrollTop]);

  return (
    <div
      className="overflow-auto"
      style={{ height: containerHeight }}
      onScroll={(e) => setScrollTop(e.target.scrollTop)}
    >
      <div style={{ height: items.length * itemHeight, position: 'relative' }}>
        {visibleItems.map((item) => (
          <div
            key={item.index}
            style={{
              position: 'absolute',
              top: item.index * itemHeight,
              height: itemHeight,
              width: '100%',
            }}
          >
            {item.content}
          </div>
        ))}
      </div>
    </div>
  );
};

// Image preloader
const ImagePreloader = ({ images }) => {
  React.useEffect(() => {
    images.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, [images]);

  return null;
};

// Intersection Observer hook for lazy loading
const useIntersectionObserver = (ref, options = {}) => {
  const [isIntersecting, setIsIntersecting] = React.useState(false);
  const [hasIntersected, setHasIntersected] = React.useState(false);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsIntersecting(entry.isIntersecting);
        if (entry.isIntersecting && !hasIntersected) {
          setHasIntersected(true);
        }
      },
      { threshold: 0.1, ...options }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [ref, hasIntersected, options]);

  return { isIntersecting, hasIntersected };
};

// Debounced search
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = React.useState(value);

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

// Performance monitoring
const PerformanceMonitor = () => {
  React.useEffect(() => {
    // Monitor Core Web Vitals
    if ('web-vital' in window) {
      import('web-vitals').then(({ getCLS, getFID, getFCP, getLCP, getTTFB }) => {
        getCLS(console.log);
        getFID(console.log);
        getFCP(console.log);
        getLCP(console.log);
        getTTFB(console.log);
      });
    }
  }, []);

  return null;
};

// Code splitting wrapper
const CodeSplitWrapper = ({ children, fallback = null }) => (
  <Suspense fallback={fallback || <div className="animate-pulse bg-gray-200 rounded h-32" />}>
    {children}
  </Suspense>
);

// Example heavy component for demonstration
const HeavyComponent = lazy(() => 
  Promise.resolve({
    default: () => (
      <div className="p-8 bg-white rounded-2xl shadow-lg">
        <h3 className="text-xl text-gray-900 mb-4 font-serif">Heavy Component</h3>
        <p className="text-gray-600 font-serif">This is a heavy component that would be lazy loaded.</p>
      </div>
    )
  })
);

export {
  MemoizedCard,
  VirtualList,
  ImagePreloader,
  useIntersectionObserver,
  useDebounce,
  PerformanceMonitor,
  CodeSplitWrapper,
  HeavyComponent,
};

